import { useEffect, useState } from "react";
import { useSessionUser } from "@/store/authStore";

/**
 * When a New badge shows (Olivia, REPLY-2026-10-06b §2): a new feature on an
 * existing surface, for 30 days after it ships or until that person first
 * uses it, whichever comes first. At most one on screen at a time, so each
 * feature that earns one is listed here.
 *
 * "Seen" is a per-viewer convenience, so it lives in localStorage, keyed per
 * user: a shared machine shows each person their own badge. Storage can be
 * missing or throw (private window, blocked site data); then the badge simply
 * shows by its date alone and nothing breaks.
 */

export const NEW_BADGE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

export const NEW_FEATURES = {
  /** Look → Tables (document themes, phase 2): released 6 October 2026. */
  lookTables: { id: "look-tables", releasedAt: "2026-10-06" },
} as const;

export type NewFeature = (typeof NEW_FEATURES)[keyof typeof NEW_FEATURES];

export function newBadgeKey(featureId: string, userId: string | null | undefined): string {
  return `lilia.new.${featureId}.seen.${userId || "anon"}`;
}

/** Within the 30 days after release, and not yet seen. */
export function newBadgeVisible(releasedAt: string, now: number, seen: boolean): boolean {
  if (seen) return false;
  const released = Date.parse(releasedAt);
  if (Number.isNaN(released)) return false;
  return now - released < NEW_BADGE_DAYS * DAY_MS;
}

export function readNewBadgeSeen(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function markNewBadgeSeen(key: string): void {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* storage unavailable: the badge shows again next time, which is harmless */
  }
}

/**
 * For a surface that IS the feature's first use (opening Look is using its
 * Tables row): decided once when the surface opens, and the open itself marks
 * it seen. So the badge shows during the first opening and not after.
 */
export function useNewBadgeOnOpen(feature: NewFeature): boolean {
  const userId = useSessionUser((s) => s.user.userId);
  const key = newBadgeKey(feature.id, userId);
  const [show] = useState(() => newBadgeVisible(feature.releasedAt, Date.now(), readNewBadgeSeen(key)));
  useEffect(() => {
    markNewBadgeSeen(key);
  }, [key]);
  return show;
}
